import _ from 'lodash';
import { store, storeActions, organizationHelper } from '~/utility/BinSentry-ui-utility';
import { LitTraceMixin } from './utility/lit-trace-mixin';
import { sharedStyles } from './styles/shared-styles';
import { css, html, LitEntity } from './utility/lit-entity';
import { IndexMixin } from './utility/index-mixin';
import { StoreMixin } from './utility/store-mixin';

import './binsentry-splash';
import './common/bn-dynamically-imported';
import './notifications/parts/notifications-active-count-icon';
import './bins/parts/bin-levels-admin';
import './map/map-index';
import './devices/device-index';
import './work-orders/work-order-index';
import './scheduler/scheduler-index';
import './review/review-index';
import './work-order-admin/work-order-admin-index';
import './work-order-locations/work-order-locations-index';
import './work-order-schedule/work-order-schedule-index';
import './imports/user-import-index';
import './imports/bin-import-index';
import './related/related-organizations-index';
import './users/user-show';

class BinSentryMain extends LitTraceMixin(IndexMixin(StoreMixin(LitEntity))) {
  static get properties() {
    return {
      currentOrgHref: { type: String, attribute: false },
      currentOrgEntity: { type: Object, attribute: false },
      primaryOrgHref: { type: String, attribute: false },
      currentUserHref: { type: String, attribute: false },
      userEntity: { type: Object, attribute: false },
      importsHref: { type: String, attribute: false },
      importsEntity: { type: Object, attribute: false },
      isMenuHidden: { type: Boolean, attribute: false },
      isProducer: { type: Boolean, attribute: false },
      queryOrgHref: { type: String, attribute: false },
      millAccountEntity: { type: Object, attribute: false },
    };
  }

  static get styles() {
    return [sharedStyles, css`
      :host {
        display: block;
        background-color: var(--background);
      }
      .page-body {
        padding: calc(var(--root-margin) / 2);
        height: calc(100% - var(--root-margin));
        position: relative;
      }
      .page-body[menu-hidden] {
        padding: 0;
        height: 100%;
        position: relative;
      }
      notifications-active-count-icon {
        margin-left: .6em;
      }
    `];
  }

  get isReady() {
    return !this.loading && this.currentOrgEntity && this.orgType
      && (!this.importsHref || this.importsEntity);
  }

  render() {
    const millHref = organizationHelper.getAccountHref(this.currentOrgEntity);
    return html`
      <lit-entity .href="${this.currentOrgHref}" @update="${this._onUpdateCurrentOrg}"></lit-entity>
      <lit-entity .href="${this.currentUserHref}" @update="${this._onUpdateUser}"></lit-entity>
      <lit-entity .href="${this.importsHref}" @update="${this._onUpdateImports}"></lit-entity>
      ${millHref ? html`<lit-entity .href="${millHref}" @update="${this._onUpdateMillAccount}"></lit-entity>` : ''}
      ${this.renderBody()}
    `;
  }

  renderBody() {
    if (!this.isReady) {
      return html`<binsentry-splash></binsentry-splash>`;
    }
    return html`
      <div class="page-body" ?menu-hidden="${this.isMenuHidden}">
        ${super.render()}
      </div>
    `;
  }

  _onUpdateCurrentOrg(e) {
    this.currentOrgEntity = e.detail.entity;
    this.__trace('BinSentryMain::onUpdateCurrentOrg', { currentOrgEntity: this.currentOrgEntity });
    if (this.currentOrgEntity) {
      this.binsHref = this.currentOrgEntity.getSubEntityByRel('https://api.binsentry.com/rel/bins')?.href;
      this.orgType = this.currentOrgEntity.getSubEntityByRel('https://api.binsentry.com/rel/organization-type').properties.name;
      this.importsHref = this.currentOrgEntity.getSubEntity('https://api.binsentry.com/rel/imports')?.href;
    } else {
      this.binsHref = undefined;
    }
  }

  _onUpdateUser(e) {
    this.userEntity = e.detail.entity;
  }

  _onUpdateImports(e) {
    this.importsEntity = e.detail.entity;
  }

  _onUpdateMillAccount(e) {
    this.millAccountEntity = e.detail.entity;
  }

  getViews() {
    this.__trace('BinSentryMain::getViews');
    const views = [];
    const accountOrgTypes = ['account', 'account-group', 'department', 'customer', 'farm', 'barn'];
    const defaultPage = this.millAccountEntity?.properties?.features?.inventoryPageAsDefault ? '/inventory' : '/customers';
    if (this.binsHref) {
      views.push(
        {
          // Redirect to ui-customers for these defaults
          default: accountOrgTypes.includes(this.orgType) || this.isProducer,
          // No name is given which causes IndexMixin to not change the location of the page
          // as we handle this ourselves in render() below.
          name: '',
          render: () => {
            // We need to wait until the call stack is cleared before redirecting as mount handlers
            // may not have run yet, causing issues when unmounting
            _.defer(() =>
              store.dispatch(storeActions.replaceLocation(defaultPage)),
            );
            return html``;
          },
        },
        {
          name: 'map',
          render: () => {
            return html`
              <map-index .href="${this.viewingOrgHref}" .path="${this.tail}"></map-index>
            `;
          },
        },
        {
          name: 'bin-levels-admin',
          render: () => {
            return html`
              <bin-levels-admin .href="${this.viewingOrgHref}" .path="${this.tail}"></bin-levels-admin>
            `;
          },
        },
      );
    }

    views.push({
      name: 'devices',
      default: this.orgType === 'dealer' || this.orgType === 'service-account',
      render: () => {
        return html`
          <device-index .href="${this.viewingOrgHref}" .path="${this.tail}"></device-index>
        `;
      },
    });

    if (this.currentOrgEntity) {
      // work orders
      const workOrdersLink = this.currentOrgEntity.getSubEntityByRel('https://api.binsentry.com/rel/work-orders');
      const workOrderLocationsLink = this.currentOrgEntity.getSubEntityByRel('https://api.binsentry.com/rel/work-order-locations');
      const userWorkOrderLocationsLinks = this.userEntity && this.userEntity.getSubEntityByRel('https://api.binsentry.com/rel/work-order-locations');

      const includeWorkOrders = workOrdersLink || workOrderLocationsLink || userWorkOrderLocationsLinks;

      views.push({
        name: 'work-order-admin',
        render: () => html`
          <work-order-admin-index .href="${this.viewingOrgHref}" .path="${this.tail}"></work-order-admin-index>
        `,
      });

      if (includeWorkOrders) {
        views.push(
          {
            name: 'work-orders',
            default: this.orgType === 'dealer-group',
            render: () => {
              return html`
                <work-order-index .href="${(workOrdersLink || {}).href}" .path="${this.tail}"></work-order-index>
              `;
            },
          },
          {
            name: 'review',
            render: () => {
              return html`
                <review-index .href="${this.viewingOrgHref}" .path="${this.tail}"></review-index>
              `;
            },
          },
        );
      }

      if (workOrderLocationsLink) {
        views.push(
          {
            name: 'work-order-locations',
            default: this.orgType === 'service-partner',
            render: () => {
              return html`
                <work-order-locations-index .href="${this.viewingOrgHref}" .path="${this.tail}"></work-order-locations-index>
              `;
            },
          },
        );
      }

      views.push(
        {
          name: 'scheduler',
          render: () => {
            return html`
              <scheduler-index .href="${this.currentOrgHref}" .path="${this.tail}"></scheduler-index>
            `;
          },
        },
      );

      views.push({
        name: 'work-order-schedule',
        render: () => {
          return html`
            <work-order-schedule-index .href="${this.currentUserHref}" .path="${this.tail}"></work-order-schedule-index>
          `;
        },
      });

      const binImportsLink = this.importsEntity?.getLink('https://api.binsentry.com/rel/bin-imports');
      if (binImportsLink) {
        views.push({
          name: 'bin-imports',
          render: () => {
            return html`
              <bin-import-index
                .href="${binImportsLink.href}"
                .path="${this.tail}"
              ></bin-import-index>
            `;
          },
        });
      }
      const userImportsLink = this.importsEntity?.getLink('https://api.binsentry.com/rel/user-imports');
      if (userImportsLink) {
        views.push({
          name: 'user-imports',
          render: () => {
            return html`
              <user-import-index
                .href="${userImportsLink.href}"
                .path="${this.tail}"
              ></user-import-index>
            `;
          },
        });
      }
    }

    if (this.orgType === 'sales-account') {
      views.push({
        name: 'related',
        default: this.orgType === 'sales-account',
        render: () => {
          return html`
            <related-organizations-index
              .href="${this.currentOrgHref}"
              .path="${this.tail}"
            ></related-organizations-index>
          `;
        },
      });
    }

    views.push({
      name: 'account',
      render: () => {
        return html`
          <user-show .href="${this.currentUserHref}" .path="${this.tail}"></user-show>
        `;
      },
    });
    if (this.currentOrgEntity && !views.find(view => view.default)) {
      views.push({
        name: 'react-orgainzations-redirect',
        default: true,
        render: () => {
          _.defer(() =>
            // rendered through React. Default view, when no default is provided
            store.dispatch(storeActions.replaceLocation('/organizations/list')),
          );
          return html``;
        },
      });
    }
    return views;
  }

  async _stateChanged({ primaryOrgHref, currentOrgHref, currentUserHref, isProducer, query, path, menuHidden }) {
    this.__trace('BinSentryMain::_stateChanged', { primaryOrgHref, currentOrgHref, currentUserHref, isProducer, path, query });

    if (this.primaryOrgHref !== primaryOrgHref || this.currentUserHref !== currentUserHref) {
      this.primaryOrgHref = primaryOrgHref;
      this.currentUserHref = currentUserHref;
    }

    if (currentOrgHref && this.currentOrgHref !== currentOrgHref) {
      this.currentOrgHref = currentOrgHref;
    }

    if (this.isProducer !== isProducer) {
      this.isProducer = isProducer;
    }

    this.queryOrgHref = query?.orgId;
    this.isMenuHidden = query?.printMode || menuHidden;
    this.viewingOrgHref = this.queryOrgHref || this.currentOrgHref || this.primaryOrgHref;
    if (this.path !== path) {
      this.path = path;
    }
  }
}

window.customElements.define('binsentry-main', BinSentryMain);
